import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Package, MapPin, Clock, PlusCircle, Inbox, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ITEM_TYPES = [
  "Blood / Pathology Tests", "COVID Test Samples", "Chemotherapy / Hazardous Drugs",
  "Pharmacy / Medication", "Medical Equipment & Devices", "Medical Records",
  "Urine Samples & Diagnostic Fluids", "Prosthetics & Implants", "Other Medical",
];
const URGENCIES = ["Immediate STAT (<60 min)", "Same-Day (within 4 hrs)", "Next-Day Route"];

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#0047FF] focus:ring-2 focus:ring-blue-100 transition-all duration-200";

const AuthCard = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    if (!res.ok) setError(res.error);
    setBusy(false);
  };

  return (
    <div data-testid="auth-card" className="w-full max-w-md rounded-[1.75rem] bg-white border border-slate-200/80 shadow-xl shadow-blue-900/10 p-8">
      <div className="flex rounded-full bg-slate-100 p-1 mb-7">
        {["login", "register"].map((m) => (
          <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                  className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all duration-200 ${
                    mode === m ? "bg-[#0047FF] text-white shadow-md shadow-blue-600/25" : "text-slate-600"
                  }`}>
            {m === "login" ? "Sign In" : "Create Account"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
        {mode === "register" && (
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Full name</label>
            <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Jordan Reyes" />
          </div>
        )}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
          <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@facility.org" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Password {mode === "register" && <span className="normal-case font-medium text-slate-400">(min 8 characters)</span>}</label>
          <input data-testid="auth-password" type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="••••••••" />
        </div>
        {error && <p data-testid="auth-error" className="text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>}
        <button data-testid="auth-submit" type="submit" disabled={busy}
                className="w-full rounded-full bg-[#0047FF] px-7 py-4 text-sm font-bold text-white shadow-xl shadow-blue-600/25 hover:bg-[#0037CC] disabled:opacity-60 transition-all duration-200">
          {busy ? "Please wait…" : mode === "login" ? "Sign In to Portal" : "Create My Account"}
        </button>
      </form>
      <p className="mt-6 text-center text-[11px] font-mono text-slate-400">
        Demo account — demo@hlmcourier.com / Demo2026!
      </p>
    </div>
  );
};

const OrderForm = ({ onCreated }) => {
  const [form, setForm] = useState({ pickup_address: "", delivery_address: "", item_type: ITEM_TYPES[0], urgency: URGENCIES[1], notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", form);
      toast.success(`Order ${data.tracking_id} dispatched`, { description: "Live tracking is now active for this shipment." });
      onCreated(data);
      setForm({ pickup_address: "", delivery_address: "", item_type: ITEM_TYPES[0], urgency: URGENCIES[1], notes: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Could not place the order."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="order-form" onSubmit={submit} className="rounded-[1.5rem] bg-white border border-slate-200/80 shadow-lg shadow-blue-900/5 p-7 space-y-4">
      <h2 className="flex items-center gap-2.5 font-display text-lg font-bold text-slate-900 tracking-tight">
        <PlusCircle className="h-5 w-5 text-[#0047FF]" /> New Order
      </h2>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Pickup address</label>
        <input data-testid="order-pickup" required minLength={5} value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} className={inputCls} placeholder="Inova Fairfax Lab, 3300 Gallows Rd, Falls Church VA" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Delivery address</label>
        <input data-testid="order-delivery" required minLength={5} value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className={inputCls} placeholder="Quest Reference Lab, Chantilly VA" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Specimen / Item type</label>
          <select data-testid="order-item-type" value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} className={inputCls}>
            {ITEM_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Urgency</label>
          <select data-testid="order-urgency" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className={inputCls}>
            {URGENCIES.map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Special instructions</label>
        <textarea data-testid="order-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} placeholder="Suite number, contact at pickup, handling notes…" />
      </div>
      <button data-testid="order-submit" type="submit" disabled={busy}
              className="w-full rounded-full bg-[#0047FF] px-7 py-4 text-sm font-bold text-white shadow-xl shadow-blue-600/25 hover:bg-[#0037CC] disabled:opacity-60 transition-all duration-200">
        {busy ? "Dispatching…" : "SUBMIT ORDER"}
      </button>
    </form>
  );
};

const ORDER_STEPS = ["Dispatched", "Picked Up", "Delivered"];

const OrderTimeline = ({ status, tracking }) => {
  const active = Math.max(0, ORDER_STEPS.indexOf(status));
  return (
    <div data-testid={`order-timeline-${tracking}`} className="mt-4 flex items-start">
      {ORDER_STEPS.map((s, i) => (
        <div key={s} className={`flex items-center ${i < ORDER_STEPS.length - 1 ? "flex-1" : ""}`}>
          <div className="flex flex-col items-center gap-1.5 shrink-0">
            <span className={`h-3.5 w-3.5 rounded-full border-2 transition-colors duration-300 ${i <= active ? "bg-[#0047FF] border-[#0047FF]" : "bg-white border-slate-300"}`} />
            <span className={`text-[9px] font-mono font-bold uppercase tracking-wider whitespace-nowrap ${i <= active ? "text-[#0047FF]" : "text-slate-400"}`}>{s}</span>
          </div>
          {i < ORDER_STEPS.length - 1 && (
            <span className={`h-0.5 flex-1 mx-2 mb-4 rounded ${i < active ? "bg-[#0047FF]" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
};

const AdminPanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/submissions").then((r) => setData(r.data)).catch(() => setData({ quotes: [], orders: [] }));
  }, []);
  const advance = async (o) => {
    try {
      const { data: res } = await api.patch(`/orders/${o.order_id}/advance`);
      toast.success(`${o.tracking_id} → ${res.status}`);
      const { data: fresh } = await api.get("/admin/submissions");
      setData(fresh);
    } catch (e) {
      toast.error(formatApiError(e, "Could not advance status."));
    }
  };
  if (!data) return null;
  return (
    <div data-testid="admin-submissions" className="mt-10 rounded-[1.5rem] bg-white border border-slate-200/80 shadow-lg shadow-blue-900/5 p-7">
      <h2 className="flex items-center gap-2.5 font-display text-lg font-bold text-slate-900 tracking-tight">
        <Inbox className="h-5 w-5 text-[#0047FF]" /> All Submissions <span className="font-mono text-xs text-slate-400 font-semibold">ADMIN</span>
      </h2>
      <div className="mt-6 grid lg:grid-cols-2 gap-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Quote / Contact Requests ({data.quotes.length})</p>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {data.quotes.map((q) => (
              <div key={q.quote_id} data-testid={`admin-quote-${q.reference}`} className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
                <p className="font-bold text-slate-900">{q.first_name} {q.last_name} <span className="font-mono text-xs text-[#0047FF] ml-2">{q.reference}</span></p>
                <p className="text-slate-600 mt-1">{q.inquiry_type} · {q.email} · {q.phone}</p>
                {q.notes && <p className="text-slate-500 mt-1 text-xs">{q.notes}</p>}
              </div>
            ))}
            {data.quotes.length === 0 && <p className="text-sm text-slate-400">No submissions yet.</p>}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Portal Orders ({data.orders.length})</p>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {data.orders.map((o) => (
              <div key={o.order_id} data-testid={`admin-order-${o.tracking_id}`} className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
                <p className="font-bold text-slate-900 font-mono">{o.tracking_id} <span className="font-sans font-semibold text-slate-500 ml-2">{o.customer_name}</span></p>
                <p className="text-slate-600 mt-1">{o.item_type} · {o.urgency}</p>
                <div className="mt-2.5 flex items-center justify-between gap-3">
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    o.status === "Delivered" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-[#0047FF]"
                  }`}>{o.status}</span>
                  {o.status !== "Delivered" && (
                    <button data-testid={`admin-advance-${o.tracking_id}`} onClick={() => advance(o)}
                            className="text-xs font-bold text-[#0047FF] hover:text-[#0037CC] transition-colors">
                      Advance status →
                    </button>
                  )}
                </div>
              </div>
            ))}
            {data.orders.length === 0 && <p className="text-sm text-slate-400">No orders yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Portal() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[#F8FAFC] bg-grid-faint">
      <header className="glass border-b border-slate-200/60 sticky top-0 z-40">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" data-testid="portal-home-link" className="flex items-center gap-3">
            <img src="/assets/hlm-logo-mark.png" alt="HLM Courier" data-testid="portal-logo" className="h-9 w-auto" />
            <span className="font-mono text-[10px] font-semibold text-slate-400 tracking-[0.2em] uppercase">Client Portal</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="portal-back-link" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-[#0047FF] transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to site
            </Link>
            {user && (
              <button data-testid="portal-logout" onClick={logout}
                      className="flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-full border border-slate-300 text-slate-700 hover:border-red-400 hover:text-red-500 transition-colors duration-200">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-14">
        {user === null && (
          <div className="py-24 flex justify-center" data-testid="portal-loading">
            <span className="h-10 w-10 rounded-full border-[3px] border-blue-100 border-t-[#0047FF] animate-spin" />
          </div>
        )}

        {user === false && (
          <div className="py-10 flex flex-col items-center">
            <p className="mb-8 text-center text-sm text-slate-500 max-w-md">
              Sign in to schedule deliveries, track live orders, and manage your account.
            </p>
            <AuthCard />
          </div>
        )}

        {user && (
          <>
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#0047FF] font-mono">Welcome back</p>
                <h1 data-testid="portal-welcome" className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">{user.name}</h1>
                <p className="text-sm text-slate-500 mt-1">{user.email} · {user.role === "admin" ? "Administrator" : "Customer"}</p>
              </div>
              <p className="font-mono text-[11px] font-semibold tracking-[0.18em] text-slate-400 uppercase flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse-dot" /> Dispatch active 24/7/365
              </p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8 items-start">
              <div className="lg:col-span-2">
                <OrderForm onCreated={(o) => setOrders((prev) => [o, ...(prev || [])])} />
              </div>
              <div className="lg:col-span-3">
                <div className="rounded-[1.5rem] bg-white border border-slate-200/80 shadow-lg shadow-blue-900/5 p-7">
                  <h2 className="flex items-center gap-2.5 font-display text-lg font-bold text-slate-900 tracking-tight">
                    <Package className="h-5 w-5 text-[#0047FF]" /> Your Orders
                  </h2>
                  <div className="mt-5 space-y-4" data-testid="orders-list">
                    {orders === null && <p className="text-sm text-slate-400">Loading…</p>}
                    {orders && orders.length === 0 && (
                      <p data-testid="orders-empty" className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-5 py-6 text-center">
                        No orders yet — place your first delivery on the left.
                      </p>
                    )}
                    {orders && orders.map((o) => (
                      <div key={o.order_id} data-testid={`order-card-${o.tracking_id}`}
                           className="rounded-2xl border border-slate-100 bg-slate-50/60 p-5 hover:border-blue-200 transition-colors duration-200">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="font-mono text-sm font-bold text-[#0047FF]">{o.tracking_id}</p>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${
                            o.status === "Delivered"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                              : "bg-blue-50 border-blue-100 text-[#0047FF]"
                          }`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${o.status === "Delivered" ? "bg-emerald-500" : "bg-[#0047FF] animate-pulse-dot"}`} /> {o.status}
                          </span>
                        </div>
                        <OrderTimeline status={o.status} tracking={o.tracking_id} />
                        <p className="mt-2.5 text-sm font-semibold text-slate-800">{o.item_type} · <span className="text-slate-500 font-medium">{o.urgency}</span></p>
                        <div className="mt-3 space-y-1.5 text-xs text-slate-500">
                          <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" /> {o.pickup_address} → {o.delivery_address}</p>
                          <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" /> {new Date(o.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {user.role === "admin" && <AdminPanel />}
          </>
        )}
      </main>
    </div>
  );
}
